/**
 * Configurator Context Orchestrator
 *
 * Provides a single entry point for setting up all configurator contexts.
 * Wires together product data, selection, pricing, and cart contexts.
 *
 * @module contexts
 */

import { setContext, getContext } from 'svelte';
import { createProductDataContext, type ProductDataContext } from './product-data.svelte';
import { createSelectionContext, type SelectionContext } from './selection.svelte';
import { createPricingContext, type PricingContext } from './pricing.svelte';
import { createCartContext, type CartContext } from './cart.svelte';

/**
 * Context keys - using symbols for type safety
 */
const PRODUCT_DATA_KEY = Symbol('product-data');
const SELECTION_KEY = Symbol('selection');
const PRICING_KEY = Symbol('pricing');
const CART_KEY = Symbol('cart');

/**
 * Combined configurator context type
 */
export interface ConfiguratorContext {
	productData: ProductDataContext;
	selection: SelectionContext;
	pricing: PricingContext;
	cart: CartContext;
}

/**
 * Setup all configurator contexts
 *
 * Creates and wires together all context modules:
 * 1. Product Data - Fetches and manages product configuration data
 * 2. Selection - Tracks user's attribute and quantity selections
 * 3. Pricing - Calculates prices based on selections
 * 4. Cart - Handles adding items to cart
 *
 * @param apiUrl - The API endpoint URL to fetch configurator data
 * @returns Combined configurator context
 *
 * @example
 * ```svelte
 * <script>
 *   import { setupConfiguratorContext } from './contexts';
 *
 *   const context = setupConfiguratorContext('/api/configurator/123');
 *
 *   // Load data on mount
 *   $effect(() => {
 *     context.productData.loadData();
 *   });
 * </script>
 * ```
 */
export function setupConfiguratorContext(apiUrl: string): ConfiguratorContext {
	// Create product data context first (no dependencies)
	const productData = createProductDataContext(apiUrl);

	// Create selection context (depends on product data)
	const selection = createSelectionContext(productData);

	// Create pricing context (depends on product data and selection)
	const pricing = createPricingContext(productData, selection);

	// Create cart context (depends on selection, product data and pricing)
	const cart = createCartContext(selection, productData, pricing);

	// Set all contexts
	setContext(PRODUCT_DATA_KEY, productData);
	setContext(SELECTION_KEY, selection);
	setContext(PRICING_KEY, pricing);
	setContext(CART_KEY, cart);

	// Return combined context
	return {
		productData,
		selection,
		pricing,
		cart,
	};
}

/**
 * Get product data context
 *
 * @returns Product data context
 * @throws Error if context is not set
 */
export function getProductDataContext(): ProductDataContext {
	const context = getContext<ProductDataContext>(PRODUCT_DATA_KEY);
	if (!context) {
		throw new Error('Product data context not found. Did you call setupConfiguratorContext?');
	}
	return context;
}

/**
 * Get selection context
 *
 * @returns Selection context
 * @throws Error if context is not set
 */
export function getSelectionContext(): SelectionContext {
	const context = getContext<SelectionContext>(SELECTION_KEY);
	if (!context) {
		throw new Error('Selection context not found. Did you call setupConfiguratorContext?');
	}
	return context;
}

/**
 * Get pricing context
 *
 * @returns Pricing context
 * @throws Error if context is not set
 */
export function getPricingContext(): PricingContext {
	const context = getContext<PricingContext>(PRICING_KEY);
	if (!context) {
		throw new Error('Pricing context not found. Did you call setupConfiguratorContext?');
	}
	return context;
}

/**
 * Get cart context
 *
 * @returns Cart context
 * @throws Error if context is not set
 */
export function getCartContext(): CartContext {
	const context = getContext<CartContext>(CART_KEY);
	if (!context) {
		throw new Error('Cart context not found. Did you call setupConfiguratorContext?');
	}
	return context;
}

/**
 * Get all configurator contexts
 *
 * @returns Combined configurator context
 * @throws Error if any context is not set
 */
export function getConfiguratorContext(): ConfiguratorContext {
	return {
		productData: getProductDataContext(),
		selection: getSelectionContext(),
		pricing: getPricingContext(),
		cart: getCartContext(),
	};
}

// Re-export types for convenience
export type { ProductDataContext } from './product-data.svelte';
export type { SelectionContext } from './selection.svelte';
export type { PricingContext } from './pricing.svelte';
export type { CartContext } from './cart.svelte';
